/**
 * 承認済み情報 — a clinic's material, from the JMC side.
 *
 * Why this exists: a clinic reporting 「本文が生成できない」 is almost always a
 * persona or a treatment left in draft, and an operator who onboards clinics
 * needs to see which one and fix it.
 *
 * THE CLINIC'S OWN SCREENS, NOT A COPY OF THEM. This panel used to carry a
 * form of its own, built from a field list, and it drifted from the clinic's
 * screens until the two asked for different things - no portrait, no AI draft,
 * no suggestions, different fields. Now each tab renders the clinic's panel
 * itself, pointed at the JMC mount of the clinic's own routes
 * (`/admin/clinics/:id/...`, see server/app.ts), so the data entry and the
 * dialogs are the same component on both sides and cannot drift again.
 *
 * What it deliberately is not: a way into the clinic's writing. Article text and
 * review findings stay behind the support elevation, with its written reason and
 * its expiry. The line is between the clinic's registered FACTS and the clinic's
 * WRITING, and only the first side is here.
 *
 * Every write is audited against the clinic with the operator as the actor. The
 * clinic remains the 広告管理責任者.
 */
import { useState } from 'react';
import type { CatalogItem, ListResponse, PlatformDefaultItem } from '../api.ts';
import type { FactScope } from '../fact-panel.ts';
import { Tabs } from './ui.tsx';
import { CatalogPanel } from '../routes/Catalog.tsx';
import { PersonasPanel } from '../routes/Personas.tsx';
import { SupervisorsPanel } from '../routes/Supervisors.tsx';
import { CtasPanel } from '../routes/Ctas.tsx';

/** The five kinds, in the order the clinic's own navigation lists them. */
export const FACT_TABS = [
  { id: 'personas', label: 'ペルソナ' },
  { id: 'treatments', label: '施術・コース・料金' },
  { id: 'supervisors', label: '監修医' },
  { id: 'cta-templates', label: 'CTAテンプレート' },
  { id: 'strengths', label: 'クリニックの強み' },
] as const;

export type FactKind = (typeof FACT_TABS)[number]['id'];

export interface ClinicFactsData {
  lists: Record<FactKind, ListResponse<CatalogItem>>;
  suggestions: ListResponse<PlatformDefaultItem>;
}

/**
 * Every kind at once, through the JMC mount. The tab counts need all five, and
 * the lists are small; 100 is the route's ceiling and the clinic's screens ask
 * for the same.
 */
export async function loadClinicFacts(
  clinicId: string,
  get: <T>(path: string) => Promise<T>,
): Promise<ClinicFactsData> {
  const base = `/admin/clinics/${clinicId}`;
  const [lists, suggestions] = await Promise.all([
    Promise.all(
      FACT_TABS.map((tab) =>
        get<ListResponse<CatalogItem>>(`${base}/${tab.id}?scope=all&limit=100`),
      ),
    ),
    get<ListResponse<PlatformDefaultItem>>(`${base}/defaults?kind=PERSONA`),
  ]);
  return {
    lists: Object.fromEntries(FACT_TABS.map((tab, at) => [tab.id, lists[at]!])) as Record<
      FactKind,
      ListResponse<CatalogItem>
    >,
    suggestions,
  };
}

export function ClinicFacts({
  clinicId,
  data,
  stores,
  /** Narrow to this store's rows plus the clinic-wide ones - the store page. */
  storeId,
}: {
  clinicId: string;
  data: ClinicFactsData;
  stores: Array<{ id: string; name: string }>;
  storeId?: string;
}) {
  const [kind, setKind] = useState<FactKind>('personas');
  const base = `/admin/clinics/${clinicId}`;
  // What the server's actForClinic resolves: clinic-admin authority, every store.
  const scope: FactScope = {
    isClinicAdmin: true,
    stores,
    activeStore: stores.find((store) => store.id === storeId),
  };

  const counts = (target: FactKind) => {
    const rows = data.lists[target].items.filter(
      (row) => !storeId || row.storeId == null || row.storeId === storeId,
    );
    return {
      approved: rows.filter((row) => row.status === 'APPROVED').length,
      total: rows.filter((row) => row.status !== 'RETIRED').length,
    };
  };

  const items = data.lists[kind].items;

  /*
   * ONE tile. The tabs and the panel they switch used to be two cards stacked,
   * which read as two unrelated sections; the tabs now sit at the top of the
   * panel's own card, above its heading. See Card's `lead`.
   */
  const lead = (
    <div className="factstabs">
      <h2 className="card__title">承認済み情報</h2>
      <p className="muted small">
        記事に書ける事実です。クリニックの画面と同じ入力・同じ確認で登録します。承認済みのものだけが生成に渡り、ここでの変更は操作者名とともに監査ログに記録されます。記事本文とチェック結果はここからは参照できません（閲覧にはサポート権限の付与が必要です）。
      </p>
      <Tabs
        active={kind}
        onChange={setKind}
        tabs={FACT_TABS.map((tab) => {
          const { approved, total } = counts(tab.id);
          return { id: tab.id, label: `${tab.label} ${approved}/${total}` };
        })}
      />
    </div>
  );
  const common = { base, scope, clinicId, lead };

  // Keyed by kind so switching tabs starts each panel clean.
  return kind === 'personas' ? (
    <PersonasPanel key={kind} items={items} suggestions={data.suggestions} {...common} />
  ) : kind === 'supervisors' ? (
    <SupervisorsPanel key={kind} items={items} {...common} />
  ) : kind === 'cta-templates' ? (
    <CtasPanel key={kind} items={items} {...common} />
  ) : (
    <CatalogPanel key={kind} items={items} resource={kind} {...common} />
  );
}